import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';

import { customersApi } from '../../api/customers.js';
import { customerDraftsApi } from '../../api/customerDrafts.js';
import { usePaginatedQuery } from '../../hooks/usePaginatedQuery.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { DataTable } from '../../components/DataTable.jsx';
import { Pagination } from '../../components/ui/Pagination.jsx';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { Badge } from '../../components/ui/Badge.jsx';

import { CustomerFormModal } from '../admin/customers/CustomerFormModal.jsx';
import { CustomerDraftsModal } from '../admin/customers/CustomerDraftsModal.jsx';

const STATUS_TONE = { active: 'success', archived: 'neutral' };
const STATUS_LABEL = { active: 'Attivo', archived: 'Archiviato' };

function fullName(c) {
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || '-';
}

/**
 * Seller-facing customer list. Customers are shared across the chain, so the
 * list is NOT store-scoped: every customer is shown, with each row marked as
 * belonging to the seller's own store or another branch. Detail navigation
 * routes under `/employee`.
 */
export function CustomersPage() {
  const { hasPermission, storeSelection } = useAuth();
  const navigate = useNavigate();

  const canCreate = hasPermission('customers:create');
  const canUpdate = hasPermission('customers:update');

  // The seller's own store(s), to flag own vs other-branch customers.
  const ownStoreIds = useMemo(
    () => new Set(((storeSelection && storeSelection.stores) || []).map((s) => String(s._id))),
    [storeSelection]
  );

  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['employee', 'customers', 'list'],
    fetcher: customersApi.list
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [resumeDraft, setResumeDraft] = useState(null);
  const [draftsOpen, setDraftsOpen] = useState(false);

  const draftsQuery = useQuery({
    queryKey: ['customer-drafts', 'list'],
    enabled: canCreate,
    queryFn: () => customerDraftsApi.list({ limit: 50 })
  });
  const draftCount = (draftsQuery.data?.items || []).length;

  const openNewCustomer = () => { setEditing(null); setResumeDraft(null); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditing(null); setResumeDraft(null); };
  const resume = (d) => { setEditing(null); setResumeDraft(d); setDraftsOpen(false); setFormOpen(true); };

  const columns = [
    {
      key: 'name',
      header: 'Cliente',
      render: (r) => (
        <button type="button" className="text-left font-medium text-ink hover:underline" onClick={() => navigate(`/employee/customers/${r._id}`)}>
          {fullName(r)}
        </button>
      )
    },
    {
      key: 'contact',
      header: 'Contatti',
      render: (r) => (
        <div className="flex flex-col">
          {r.email && <span className="text-xs text-ink-muted">{r.email}</span>}
          {r.phone && <span className="text-xs text-ink-muted">{r.phone}</span>}
          {!r.email && !r.phone && <span className="text-xs text-ink-faint">-</span>}
        </div>
      )
    },
    {
      key: 'store',
      header: 'Negozio',
      render: (r) => {
        const store = r.storeId && (r.storeId.name || r.storeId.code);
        const id = r.storeId && (r.storeId._id || r.storeId);
        const own = id && ownStoreIds.has(String(id));
        if (!store) return <span className="text-xs text-ink-faint">-</span>;
        return <Badge tone={own ? 'success' : 'neutral'}>{own ? `${store} · tuo` : store}</Badge>;
      }
    },
    {
      key: 'account',
      header: 'Account',
      render: (r) => r.userId ? <Badge tone="info">collegato</Badge> : <span className="text-xs text-ink-faint">-</span>
    },
    {
      key: 'status',
      header: 'Stato',
      render: (r) => <Badge tone={STATUS_TONE[r.status] || 'neutral'}>{STATUS_LABEL[r.status] || r.status}</Badge>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-3">
          <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => navigate(`/employee/customers/${r._id}`)}>Scheda</button>
          {canUpdate && <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => { setEditing(r); setFormOpen(true); }}>Modifica</button>}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Clienti & animali"
        description="I clienti di tutti i negozi della catena. La scheda completa (animali e consulenze) resta limitata al tuo negozio."
        actions={canCreate ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setDraftsOpen(true)}>
              Bozze{draftCount > 0 ? ` (${draftCount})` : ''}
            </Button>
            <Button onClick={openNewCustomer}>
              <UserPlus className="mr-1.5 h-4 w-4" />Nuovo cliente
            </Button>
          </div>
        ) : null}
      />

      <div className="flex flex-wrap items-end gap-3">
        <Input
          className="w-72"
          label="Cerca"
          placeholder="nome, email, telefono..."
          value={filters.q || ''}
          onChange={(e) => setFilters({ q: e.target.value || undefined })}
        />
        <Select label="Stato" className="w-44" value={filters.status || ''} onChange={(e) => setFilters({ status: e.target.value || undefined })}>
          <option value="">Tutti</option>
          <option value="active">Attivi</option>
          <option value="archived">Archiviati</option>
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: 'Nessun cliente', description: 'Registra la prima scheda cliente del negozio.' }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <CustomerFormModal open={formOpen} onClose={closeForm} initial={editing} resumeDraft={resumeDraft} />
      <CustomerDraftsModal open={draftsOpen} onClose={() => setDraftsOpen(false)} onResume={resume} />
    </div>
  );
}
